import { useQuery } from '@apollo/client';
import { Box, DialogActions, styled } from '@mui/material';
import { BlockContainer } from 'FeatureModules/Activity/Components/Forms/styles';
import Filter from 'Components/Filter/Filter';
import Loader from 'Components/Forms/Loader';
import useDialog from 'hooks/useDialog';
import { DialogInner } from 'FeatureModules/Partner/Components/Partners.styles';
import SiteSelectionTable from 'FeatureModules/Site/Components/SiteTable/SiteSelectionTable';
import { DialogDefault, ThemeButton } from 'DesignSystem';
import { GET_CLUSTER_SITE_IDS } from 'graphql/secure/queries/site.queries';
import useSites from 'FeatureModules/Site/hooks/useSites';
import React, { FC, useEffect, useMemo, useState } from 'react';
import { IDefaultDialogProps } from 'types/dialog.types';
import { IBasePartnerCompany } from 'FeatureModules/Partner/types/partner.types';
import { ESiteType, ESiteViewOption, IBaseClusterSite } from 'FeatureModules/Site/types/site.types';
import { IGraphQlConnection } from 'types/core.types';
import { removeGraphConnections } from 'utils/graphql.utils';
import useSiteMutations from '../../hooks/useSiteMutations';
import ManageSiteSummary, { TManageSiteErrors } from './ManageSiteSummary';
import ErrorState from 'Components/ErrorState';
import FlexBox from 'Components/FlexBox';

interface IManageClusterSitesDialogProps extends IDefaultDialogProps {
  siteCluster: IBaseClusterSite;
  partner?: IBasePartnerCompany;
}

const StyledBlockContainer = styled(BlockContainer)({
  paddingTop: 0,
  height: '100%',
});

const ManageClusterSitesDialog: FC<IManageClusterSitesDialogProps> = ({
  onClose,
  open,
  siteCluster,
}) => {
  const [selectedSiteIds, setSelectedSiteIds] = useState<string[]>([]);

  const { updateSite, loading: loadingMutation } = useSiteMutations();
  const { closeDialog: onCloseAll } = useDialog();

  const {
    sites,
    loading,
    hasNextPage,
    totalCount,
    searchTerm,
    filters,
    handlePageEndReached,
    handleSearchTermChange,
    handleFilterChange,
    handleFilterReset,
  } = useSites({
    skipUseQueryParams: true,
    defaultViewOption: ESiteViewOption.LIST,
    defaultSiteTypes: [
      ESiteType.FARM,
      ESiteType.COLLECTION_SITE,
      ESiteType.FOREST,
      ESiteType.GENERIC_SITE,
      ESiteType.MINE,
      ESiteType.ORIGIN_SITE,
    ],
  });

  const {
    data: clusterSiteIdsData,
    loading: clusterSiteIdsDataLoading,
    error,
  } = useQuery<{ site: { sites: IGraphQlConnection<{ id: string }> } }>(GET_CLUSTER_SITE_IDS, {
    variables: { id: siteCluster.id },
  });

  const firstSelectedSite = useMemo(() => {
    if (selectedSiteIds.length) {
      return sites.find(site => site.id === selectedSiteIds[0]);
    }
  }, [sites, selectedSiteIds]);

  const errors = useMemo(() => {
    // Enable button if no sites are selected
    if (selectedSiteIds.length === 0) return {};

    return selectedSiteIds?.reduce((prevErrors, id) => {
      const site = sites.find(s => s.id === id);
      if (!site) {
        return prevErrors; // Skip validation if site is not found because of pagination
      }

      // Check if all sites have the same siteType
      if (
        (siteCluster.sitesType && siteCluster.sitesType !== site.siteType) ||
        (!siteCluster.sitesType && firstSelectedSite?.siteType !== site.siteType)
      ) {
        prevErrors = { ...prevErrors, siteType: 'Invalid site type' };
      }

      // Check if all sites have the same raw material
      if (
        (siteCluster.sitesType === ESiteType.FARM &&
          site.siteType === ESiteType.FARM &&
          siteCluster.sitesRawMaterial &&
          siteCluster.sitesRawMaterial.id !== site.rawMaterial.id) ||
        (!siteCluster.sitesType &&
          firstSelectedSite?.siteType === ESiteType.FARM &&
          site.siteType === ESiteType.FARM &&
          firstSelectedSite.rawMaterial.id !== site.rawMaterial.id &&
          ((siteCluster.sitesRawMaterial &&
            site.rawMaterial.id !== siteCluster.sitesRawMaterial.id) ||
            !siteCluster.sitesRawMaterial))
      ) {
        prevErrors = { ...prevErrors, rawMaterial: 'Invalid raw material' };
      }

      // Check if all sites are in the same country
      if (
        (siteCluster.countryCode && siteCluster.countryCode !== site.countryCode) ||
        (firstSelectedSite && firstSelectedSite.countryCode !== site?.countryCode)
      ) {
        prevErrors = { ...prevErrors, country: 'Invalid country' };
      }

      return prevErrors;
    }, {} as TManageSiteErrors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSiteIds, siteCluster, sites, firstSelectedSite]);

  const hasErrors = Object.keys(errors).length > 0;

  const handleAddSites = async () => {
    await updateSite({
      variables: {
        id: siteCluster.id,
        input: {
          siteType: ESiteType.CLUSTER,
          siteClusterInput: {
            siteIds: selectedSiteIds,
          },
        },
      },
    });
    onCloseAll?.();
  };

  useEffect(() => {
    // select sites that are already in the cluster
    if (clusterSiteIdsData?.site.sites.edges?.length) {
      const siteClusterIds = removeGraphConnections(clusterSiteIdsData.site.sites).map(
        ({ id }) => id
      );
      setSelectedSiteIds(siteClusterIds);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clusterSiteIdsData]);

  const handleSiteSelectionToggled = (siteId: string) => {
    if (selectedSiteIds.includes(siteId)) {
      setSelectedSiteIds(selectedSiteIds?.filter(id => id !== siteId));
    } else {
      setSelectedSiteIds([...selectedSiteIds, siteId]);
    }
  };

  return (
    <DialogDefault
      title="Manage sites in cluster"
      onClose={onClose}
      open={open}
      fullWidth
      iconName="site-cluster-manage"
      maxWidth="xl"
      background="#fff"
    >
      <DialogInner container minHeight={620}>
        {clusterSiteIdsDataLoading ? (
          <Loader />
        ) : error || !clusterSiteIdsData ? (
          <ErrorState />
        ) : (
          <>
            <StyledBlockContainer container z-index={0}>
              <ManageSiteSummary
                site={siteCluster}
                requiredSiteType={firstSelectedSite?.siteType || siteCluster.sitesType}
                requiredRawMaterialTitle={
                  siteCluster.sitesRawMaterial?.title ||
                  (firstSelectedSite?.siteType === ESiteType.FARM
                    ? firstSelectedSite?.rawMaterial.title
                    : undefined)
                }
                requiredLocationName={siteCluster.locationName}
                errors={errors}
              />

              <FlexBox width="100%" gap={2} flexDirection="column" alignItems="normal">
                <Filter
                  filters={filters}
                  onFilterChange={handleFilterChange}
                  onFilterReset={handleFilterReset}
                />

                <SiteSelectionTable
                  maxHeight={400}
                  loading={loading}
                  sites={sites}
                  totalCount={totalCount}
                  hasMoreRows={hasNextPage}
                  hideActions
                  searchTerm={searchTerm}
                  selectedSiteIds={selectedSiteIds}
                  onSiteSelectionToggle={handleSiteSelectionToggled}
                  onEndReached={handlePageEndReached}
                  onSearchTermChange={handleSearchTermChange}
                />
              </FlexBox>
            </StyledBlockContainer>
          </>
        )}
      </DialogInner>

      <DialogActions sx={{ width: '100%' }}>
        <Box display="flex" justifyContent="space-between" width="100%">
          <ThemeButton color="BLUE_ICE" size="large" type="button" onClick={onClose}>
            Cancel
          </ThemeButton>
          <ThemeButton
            color="PRIMARY"
            size="large"
            type="submit"
            disabled={hasErrors}
            tooltip={hasErrors ? 'Please resolve the errors before saving' : undefined}
            loading={loadingMutation}
            onClick={handleAddSites}
          >
            Save selection {(selectedSiteIds.length && `(${selectedSiteIds.length})`) || ''}
          </ThemeButton>
        </Box>
      </DialogActions>
    </DialogDefault>
  );
};

export default ManageClusterSitesDialog;
